/** Same key as ops-core so preference survives across apps on one origin. */
export const UI_MODE_STORAGE_KEY = 'intelaccounts_ui_mode';
const MODERN_UI_BY_DEFAULT_STORAGE_KEY = 'intelaccounts_enable_modern_ui_by_default';
const UI_MODE_USER_CHOICE_KEY = 'intelaccounts_ui_mode_user_choice';
const MODERN_SESSION_REFRESH_PENDING_KEY = 'intelaccounts_modern_session_refresh_pending';
const TAB_OPENED_MODERN_URL_KEY = 'intelaccounts_tab_opened_modern_url';

export type UiModePreference = 'modern' | 'classic';

const PUBLIC_PATH_PREFIXES = ['/login', '/my-companies', '/login-v2'];

/**
 * Routes implemented in the modern ops-core SPA (subset of tya-operation).
 * Used so "Modern UI" / href helpers do not send users to ops-core with a path it cannot render.
 */
const OPS_CORE_ROUTE_PREFIXES = [
  '/raisepr',
  '/raise-draft-pr',
  '/update-pr',
  '/view-pr',
  '/view-approval',
  '/rejected-pr',
  '/allpr-approvals',
  '/all-pr-approval',
  '/draft-pr',
  '/hold-pr-for-additional-info',
  '/close-pr',
  '/pr-approved-by-me',
  '/prapproval-summary',
  '/my-purchases-pr',
  '/accepted-items',
  '/return-to-store-acceptance',
  // PO module routes
  '/raise-po',
  '/direct-po',
  '/raise-draft-po',
  '/catalogue-based-po',
  '/raise-po-multi-vendor',
  '/raise-po-for-quotation',
  '/raise-po-multi-vendor-multi-uom',
  '/bulk-po-import',
  '/direct-po-import',
  '/recurring-po',
  '/copy-po',
  '/draft-po',
  '/update-po',
  '/rejected-po',
  '/my-orders',
  '/po-approval',
  '/poapproval-summary',
  '/hold-po-for-additional-info',
  '/close-po',
  '/cancel-po',
  '/modify-po',
  '/po-modification-request',
  '/material-readiness-po',
  '/additional-po-attachment',
  '/vendor-orders',
  '/po-advance-payment',
  '/payment-approval',
  '/advance-payments',
  '/update-advance-payment-utr',
  '/completed-po-payment',
  '/po-advance-payment-approval-summary',
  '/my-po-excess-budget',
  '/po-excess-budget-approvals-summary',
  '/po-excess-budget-for-approvals',
  '/vendor-po-summary',
  '/vendor-pending-po',
  '/vendor-fulfilled-po',
  '/vendor-rejected-po',
  '/vendor-mdcc-po',
  // D/C Note module routes
  '/add-debit-credit-note',
  '/update-debit-credit-note',
  '/my-debit-credit',
  '/all-debit-credit',
  '/debit-credit-by-vendor',
  '/debit-credit-for-approvals',
  '/debit-credit-approvals-summary',

  /** Invoice module (app-routing.module.ts) — required or menu/API redirects leave ops-core → blank classic shell. */
  '/add-ddc-invoice-payments',
  '/add-invoice-payments',
  '/add-invoice-v2',
  '/add-invoice-with-po-advance-tds',
  '/addinvoice',
  '/all-approved-invoices',
  '/all-hold-invoice',
  '/all-invoices',
  '/all-recurring-inv',
  '/approved-generate-invoice-android-summary',
  '/approved-summary-recurring-inv',
  '/auto-generated-inv',
  '/completed-invoice-payment',
  '/copy-new-direct-invoice',
  '/create-recurring-inv',
  '/direct-invoice',
  '/direct-invoices',
  '/draft-invoice',
  '/draft-recurring-inv',
  '/generate-invoice-android',
  '/generate-invoice-android-approval',
  '/grn-invoice-file',
  '/grn-invoice-upload-summary',
  '/hold-invoice-for-additional-info',
  '/hold-invoice-payment',
  '/hold-invoice-payment-for-additional-info',
  '/hold-vendor-invoices',
  '/invoice-approval',
  '/invoice-dashboard',
  '/invoice-data',
  '/invoice-debit-note',
  '/invoice-history',
  '/invoice-my-approved-summary',
  '/invoice-payment-approval',
  '/invoice-payments',
  '/invoice-summary',
  '/invoices',
  '/mailed-invoice',
  '/mailed-invoice-cc',
  '/multi-grn-invoice',
  '/my-approved-invoice-summary',
  '/my-generate-invoice-android',
  '/my-invoices',
  '/my-invoices-summary',
  '/my-mailed-invoice',
  '/raise-direct-invoice',
  '/raise-draft-direct-invoice',
  '/raise-recurring-direct-invoice',
  '/raise-rejected-inv',
  '/raise-rejected-invoice',
  '/recurring-inv-for-approval',
  '/rejected-invoice',
  '/rejected-vendor-invoice',
  '/rejected-vendor-invoice-dashboard',
  '/removed-mailed-invoice',
  '/removed-my-mailed-invoice',
  '/scan-invoice',
  '/test-direct-invoice',
  '/update-invoice',
  '/update-recurring-inv',
  '/vendor-add-invoice',
  '/vendor-direct-invoice',
  '/vendor-invoice-summary',
  '/vendor-my-invoice',
  '/view-invoice',
  '/zero-touch-invoice',
  // Invoice Payments module routes
  '/add-payment',
  '/payment-summary',
  '/all-approved-payments',
  '/add-direct-payment',
  '/my-direct-payment',
  '/all-direct-payment',
  '/direct-payment-for-approval',
  '/direct-payment-approval-summary',
  '/retention-payment',
  '/my-retention-payment',
  '/retention-payment-for-approval',
  '/retention-payment-approval-summary',
  '/part-payment',
  '/my-part-payment',
  '/part-payment-for-approval',
  '/part-payment-approval-summary',
  '/add-employee-advance-payment',
  '/completed-employee-advance-payment',
  '/employee-advance-payment-for-approval',
  '/employee-advance-payment-approval-summary',
  '/update-employee-advance-payment-utr',
  '/new-irn',
  '/new-srn',
  '/myirn-summary',
  '/all-open-irn',
  '/add-grn',
  '/grn-summary',
  '/allocate-grn-rack',
  '/allocated-grn-rack',
  '/hold-grn',
  '/my-grn-excess',
  '/grn-excess-for-approvals',
  '/grn-excess-approvals-summary',
];

export function readUiModePreference(): UiModePreference {
  if (typeof localStorage === 'undefined') {
    return 'classic';
  }
  const stored = localStorage.getItem(UI_MODE_STORAGE_KEY);
  const v = String(stored != null ? stored : '').toLowerCase();
  return v === 'modern' ? 'modern' : 'classic';
}

export function writeUiModePreference(mode: UiModePreference): void {
  if (typeof localStorage === 'undefined') {
    return;
  }
  localStorage.setItem(UI_MODE_STORAGE_KEY, mode);
}

/** Save company meta `enable_modern_ui_by_default` when the API sends it. */
export function rememberModernUiByDefault(response: { enable_modern_ui_by_default?: any } | null | undefined): void {
  if (typeof localStorage === 'undefined' || !response || !Object.prototype.hasOwnProperty.call(response, 'enable_modern_ui_by_default')) {
    return;
  }
  const value = response.enable_modern_ui_by_default;
  if (value === undefined || value === null || value === '') {
    return;
  }
  const enabled = value === true || value === 1 || value === '1';
  localStorage.setItem(MODERN_UI_BY_DEFAULT_STORAGE_KEY, enabled ? '1' : '0');
}

/** Remember a Classic / Modern click so the company default does not override it. */
export function writeUiModeUserChoice(mode: UiModePreference): void {
  writeUiModePreference(mode);
  if (typeof localStorage === 'undefined') {
    return;
  }
  localStorage.setItem(UI_MODE_USER_CHOICE_KEY, mode);
}

export function readUiModeUserChoice(): UiModePreference | null {
  if (typeof localStorage === 'undefined') {
    return null;
  }
  const choice = localStorage.getItem(UI_MODE_USER_CHOICE_KEY);
  if (choice === 'modern' || choice === 'classic') {
    return choice;
  }
  return null;
}

/** Remember that this tab opened the modern URL, and ask the modern app to refresh its session. */
export function markTabOpenedModernUrl(): void {
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(MODERN_SESSION_REFRESH_PENDING_KEY, '1');
  }
  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.setItem(TAB_OPENED_MODERN_URL_KEY, '1');
  }
}

/**
 * The modern app writes intelaccounts_ui_mode = "classic" before it sends the user back.
 * Record that as a Classic click before any URL sync overwrites the stored mode.
 * A return while the stored mode is still "modern" is not a Classic click.
 */
export function recordClassicChoiceOnReturnFromModern(): void {
  if (typeof sessionStorage === 'undefined' || typeof localStorage === 'undefined') {
    return;
  }
  if (sessionStorage.getItem(TAB_OPENED_MODERN_URL_KEY) !== '1') {
    return;
  }
  sessionStorage.removeItem(TAB_OPENED_MODERN_URL_KEY);
  if (localStorage.getItem(UI_MODE_STORAGE_KEY) === 'classic') {
    writeUiModeUserChoice('classic');
  }
}

function prefersModernOpening(): boolean {
  const choice = readUiModeUserChoice();
  if (choice) {
    return choice === 'modern';
  }
  return typeof localStorage !== 'undefined' && localStorage.getItem(MODERN_UI_BY_DEFAULT_STORAGE_KEY) === '1';
}

/** Page can open in Modern UI, and the user has not chosen Classic. */
export function isModernDefaultCandidate(path: string): boolean {
  if (readUiModeUserChoice() === 'classic' || (typeof window !== 'undefined' && isOpsCorePathnameShell())) {
    return false;
  }
  const raw = (path || '/').trim() || '/';
  const qIndex = raw.indexOf('?');
  const logical = stripOpsCoreFromPath((qIndex >= 0 ? raw.slice(0, qIndex) : raw) || '/');
  if (isPublicAuthBypassPath(logical) || logical === '/' || logical === '/dashboard' || !isOpsCoreSupportedRoute(logical)) {
    return false;
  }
  return true;
}

/**
 * Leave Classic for Modern when the company default (or an explicit Modern click) says so.
 * Returns true when navigation has started.
 */
export function openModernShellIfDefault(path: string): boolean {
  if (!prefersModernOpening() || typeof window === 'undefined' || isOpsCorePathnameShell()) {
    return false;
  }
  const raw = (path || '/').trim() || '/';
  const qIndex = raw.indexOf('?');
  const logical = stripOpsCoreFromPath((qIndex >= 0 ? raw.slice(0, qIndex) : raw) || '/');
  const qs = qIndex >= 0 ? raw.slice(qIndex) : '';
  // Dashboard is Classic-only, so the default applies only on routes that exist in Modern UI.
  if (isPublicAuthBypassPath(logical) || logical === '/' || logical === '/dashboard' || !isOpsCoreSupportedRoute(logical)) {
    return false;
  }
  const hashSeg = logical.replace(/^\//, '').replace(/^\/+/, '');
  writeUiModePreference('modern');
  markTabOpenedModernUrl();
  window.location.href = window.location.origin + '/ops-core/#/' + hashSeg + qs;
  return true;
}

export function pathHasOpsCorePrefix(path: string): boolean {
  const p = (path || '').trim();
  return p === '/ops-core' || p.startsWith('/ops-core/');
}

/**
 * Modern QA/prod can serve app under pathname `/ops-core` while hash is flat (`#/raisepr`).
 */
export function isOpsCorePathnameShell(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  const pathname = (window.location.pathname || '').replace(/\/+$/, '');
  return pathname === '/ops-core' || pathname.startsWith('/ops-core/');
}

/**
 * Build hash URL while preserving `/ops-core` pathname shell for modern mode.
 * Set `useRootPathname` for forcing classic root shell.
 */
export function hrefForAppHashPath(path: string, options?: { useRootPathname?: boolean }): string {
  if (typeof window === 'undefined') {
    const p = (path || '/').trim() || '/';
    return p.startsWith('/') ? p : `/${p}`;
  }
  const raw = (path || '/').trim() || '/';
  const qIndex = raw.indexOf('?');
  const pathPart = (qIndex >= 0 ? raw.slice(0, qIndex) : raw).trim();
  const qs = qIndex >= 0 ? raw.slice(qIndex) : '';
  const p = pathPart.startsWith('/') ? pathPart : `/${pathPart}`;
  const logicalPath = stripOpsCoreFromPath(p.split('?')[0] || '/');
  const pathForHash =
    logicalPath === '/' || logicalPath === '/ops-core' ? '/dashboard' : logicalPath;
  const hashBody =
    pathForHash.replace(/^\//, '').replace(/^\/+/, '') || 'dashboard';
  const useRoot = options && options.useRootPathname === true;
  // Only keep `/ops-core` pathname for routes the modern SPA actually implements (parity with ops-core).
  if (!useRoot && isOpsCorePathnameShell() && isOpsCoreSupportedRoute(logicalPath)) {
    return `${window.location.origin}/ops-core/#/${hashBody}${qs}`;
  }
  return `${window.location.origin}/#/${hashBody}${qs}`;
}

export function stripOpsCoreFromPath(path: string): string {
  const p = (path || '').trim() || '/';
  if (p === '/ops-core') {
    return '/dashboard';
  }
  if (p.startsWith('/ops-core/')) {
    const stripped = '/' + p.slice('/ops-core/'.length);
    return stripped === '/' ? '/dashboard' : stripped;
  }
  return p.startsWith('/') ? p : '/' + p;
}

/** Whether a logical hash path exists in the ops-core router (not necessarily in tya-operation). */
export function isOpsCoreSupportedRoute(path: string): boolean {
  const logical = stripOpsCoreFromPath((path || '/').split('?')[0] || '/');
  if (logical === '/' || logical === '/ops-core') {
    return true;
  }
  return OPS_CORE_ROUTE_PREFIXES.some(
    (prefix) => logical === prefix || logical.startsWith(prefix + '/')
  );
}
/** Show Classic / Modern toggle only on routes listed in OPS_CORE_ROUTE_PREFIXES. */
export function shouldShowUiModeToggle(path: string): boolean {
  if (isPublicAuthBypassPath(path)) {
    return false;
  }
  return isOpsCoreSupportedRoute(path);
}

export function isPublicAuthBypassPath(path: string): boolean {
  const p = stripOpsCoreFromPath(path.split('?')[0] || '/');
  return PUBLIC_PATH_PREFIXES.some((prefix) => p === prefix || p.startsWith(prefix + '/'));
}

/** Use after login / company pick / auth failures so preference is respected. */
export function dashboardPathForAuthRedirect(): string {
  if (isOpsCorePathnameShell()) {
    return '/dashboard';
  }
  return '/dashboard';
}

export function prefixedPathForModern(logicalPath: string): string {
  const logical = stripOpsCoreFromPath((logicalPath.split('?')[0] || '/').trim());
  if (logical === '/' || logical === '/dashboard') {
    return '/ops-core/dashboard';
  }
  return '/ops-core' + logical;
}

export function applyUiModeToRedirectPath(url: string): string {
  const raw = String(url != null ? url : '').trim();
  if (!raw) {
    if (isOpsCorePathnameShell()) {
      return '/dashboard';
    }
    return '/dashboard';
  }
  const qIndex = raw.indexOf('?');
  const pathPart = (qIndex >= 0 ? raw.slice(0, qIndex) : raw).trim();
  const qs = qIndex >= 0 ? raw.slice(qIndex) : '';
  const normalized = pathPart.charAt(0) === '/' ? pathPart : '/' + pathPart;
  if (isPublicAuthBypassPath(normalized)) {
    return normalized + qs;
  }
  const logical = stripOpsCoreFromPath(normalized);
  // In `/ops-core` pathname shell, router uses logical hash paths only.
  if (isOpsCorePathnameShell()) {
    return logical + qs;
  }
  // Explicit ops-core routes always stay modern.
  if (pathHasOpsCorePrefix(normalized)) {
    return prefixedPathForModern(logical) + qs;
  }
  // Non-prefixed routes stay in classic shell.
  return logical + qs;
}



// WEBPACK FOOTER //
// /codebuild/output/src1914368402/src/src/app/shared/utils/ui-mode.ts